"use client";

import Image from "next/image";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

/**
 * Program Benefits 1040 — BDU Programme Roadmap ("Key Program Features").
 *
 * Port of BDU v1 `components/Degree/Roadmap.tsx`: a full-bleed background image,
 * a centred heading with a gradient rule, and a three-up row of icon / title /
 * description columns.
 *
 * BREAKPOINTS — v1 `md:` (768px) is `tab:` here (this app remaps `md:` to 1230px).
 *
 * ROW HEIGHT — the reference measures every card title, groups them by `offsetTop`
 * (i.e. by visual row) and applies the tallest title height in each row as a
 * `min-height`, so descriptions line up across a row even when titles wrap to a
 * different number of lines. That behaviour is reproduced here.
 *
 * Distinct from Program Benefits 1029 (the home Key Benefits): light background art
 * with no tint, a gradient rule instead of a flat one, and 72px icons.
 */

interface RoadmapRow {
  id?: string | number;
  meta_image?: string;
  meta_alt_image?: string;
  meta_title?: string;
  meta_sub_title?: string;
  meta_priority?: number;
  meta_status?: boolean;
}

interface ProgramBenefits1040Data {
  metadata?: RoadmapRow[];
  title?: string;
  title_start_color?: string;
  /** Rule under the heading */
  gradient_color_1?: string;
  gradient_color_2?: string;
  /** Full-bleed background art */
  section_background_image?: string;
  /** Card title + description colour */
  description_color?: string;
  status?: boolean;
}

const ProgramBenefits1040 = ({ data }: { data: any }) => {
  const sectionData = (data?.data ?? data) as
    | ProgramBenefits1040Data
    | undefined;

  const {
    title = "",
    title_start_color = "#222023",
    gradient_color_1 = "#13344F",
    gradient_color_2 = "#77B6EB",
    section_background_image = "",
    description_color = "#222023",
  } = sectionData || {};

  const items = useMemo(() => {
    const rows = sectionData?.metadata;
    if (!Array.isArray(rows)) return [];
    return rows
      .filter((r) => r?.meta_status !== false)
      .sort(
        (a, b) =>
          (Number(a?.meta_priority) || 0) - (Number(b?.meta_priority) || 0),
      );
  }, [sectionData?.metadata]);

  // Per-row tallest title height, keyed by offsetTop.
  const titleRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [rowHeights, setRowHeights] = useState<Record<number, number>>({});

  const measure = useCallback(() => {
    const rows: Record<number, HTMLDivElement[]> = {};
    titleRefs.current.forEach((el) => {
      if (!el) return;
      const top = el.offsetTop;
      if (!rows[top]) rows[top] = [];
      rows[top].push(el);
    });
    const next: Record<number, number> = {};
    Object.entries(rows).forEach(([top, els]) => {
      next[Number(top)] = Math.max(...els.map((el) => el.offsetHeight));
    });
    setRowHeights(next);
  }, []);

  useEffect(() => {
    const id = window.setTimeout(measure, 50);
    window.addEventListener("resize", measure);
    return () => {
      window.clearTimeout(id);
      window.removeEventListener("resize", measure);
    };
  }, [measure, items]);

  if (!sectionData?.status || items.length === 0) return null;

  return (
    <div
      // The reference only sets `md:w-full`, which is a no-op there because the
      // section is a block element. This app's <main> is `flex flex-col items-center`,
      // so the width has to be explicit at every size.
      className="flex h-full w-full items-center justify-center bg-cover bg-center bg-no-repeat px-[50px] pb-[42px] pt-[42px] tab:px-[88px] tab:pb-[60px]"
      style={
        section_background_image
          ? { backgroundImage: `url('${section_background_image}')` }
          : undefined
      }
    >
      <div className="flex flex-col items-center justify-center gap-[35px] tab:w-full tab:gap-[50px]">
        <div className="flex w-full flex-col items-center justify-center gap-[18px] tab:gap-5">
          <h2
            className="text-center text-2xl font-bold tab:text-[28px]"
            style={{ color: title_start_color }}
          >
            {title}
          </h2>
          <div
            className="h-[3px] w-[154px] tab:w-[148px]"
            style={{
              backgroundImage: `linear-gradient(to right, ${gradient_color_1}, ${gradient_color_2})`,
            }}
          />
        </div>

        <div className="flex w-full flex-col flex-wrap items-center justify-center gap-9 tab:flex-row tab:items-start tab:gap-x-11 tab:gap-y-9">
          {items.map((item, idx) => {
            const top = titleRefs.current[idx]?.offsetTop ?? 0;
            const minHeight = rowHeights[top];

            return (
              <div
                key={item.id ?? idx}
                className="flex flex-col items-center justify-center gap-2 text-center tab:w-[calc(33.33%-40px)]"
                style={{ color: description_color }}
              >
                <div className="flex items-center justify-center">
                  {item.meta_image && (
                    <Image
                      src={item.meta_image}
                      alt={item.meta_alt_image || item.meta_title || "Icon"}
                      width={72}
                      height={72}
                      loading="lazy"
                    />
                  )}
                </div>
                <div
                  className="flex items-center justify-center"
                  style={{ minHeight }}
                  ref={(el) => {
                    titleRefs.current[idx] = el;
                  }}
                >
                  <p className="text-sm font-bold tab:text-lg">
                    {item.meta_title}
                  </p>
                </div>
                <p className="text-sm font-normal">{item.meta_sub_title}</p>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default ProgramBenefits1040;
